import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FiTrendingUp,
  FiCreditCard,
  FiFileText,
  FiAlertCircle,
  FiBox,
  FiShoppingCart,
  FiInbox,
  FiArrowRight,
  FiPieChart,
  FiDollarSign,
  FiClock,
  FiCheckCircle,
  FiActivity,
  FiFilter,
} from 'react-icons/fi';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/ui/PageHeader';
import { Card, CardHeader, CardBody } from '../components/ui/Card';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import CurrencyDisplay from '../components/shared/CurrencyDisplay';
import { Select } from '../components/ui/Input';
import { useAuth } from '../auth/AuthContext';
import { api, unwrap } from '../api/client';
import { listPendingApprovals } from '../api/workflows';
import { listInvoices } from '../api/invoices';
import { filterNavForUser } from '../components/layout/navConfig';

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtShort(v) {
  if (!v) return '₹0';
  if (v >= 1e7) return '₹' + (v / 1e7).toFixed(1) + 'Cr';
  if (v >= 1e5) return '₹' + (v / 1e5).toFixed(0) + 'L';
  if (v >= 1000) return '₹' + (v / 1000).toFixed(0) + 'K';
  return '₹' + v;
}

function KpiCard({ label, value, icon: Icon, tone = 'blue', subtitle, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-blue-200 w-full"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
          <Icon className="text-lg" />
        </div>
      </div>

      <div className="mt-3">
        <div className="text-2xl font-bold tracking-tight text-slate-900">{value}</div>
        {subtitle && (
          <div className="mt-1 flex items-center gap-1 text-xs font-medium text-slate-500">
            {subtitle}
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center text-xs font-semibold text-blue-600 opacity-80 group-hover:opacity-100">
        <span>View Details</span>
        <FiArrowRight className="ml-1 transition-transform group-hover:translate-x-1" />
      </div>
    </button>
  );
}

function SuperAdminDashboard() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [productName, setProductName] = useState('');

  const { data: products = [] } = useQuery({
    queryKey: ['products', 'list-simple'],
    queryFn: async () => {
      const res = await api.get('/product/getAllProducts');
      const data = res.data || [];
      return data.map(p => ({ id: p.id, name: p.productname || p.productName }));
    },
  });

  const { data: revenueData, isLoading: isRevLoading } = useQuery({
    queryKey: ['revenue', 'dashboard', productName],
    queryFn: async () => {
      const endpoint = productName 
        ? `/finance/revenue/dashboard/by-product?productName=${encodeURIComponent(productName)}`
        : '/finance/revenue/dashboard';
      const res = await api.get(endpoint);
      return unwrap(res);
    },
  });

  const { data: expenseData, isLoading: isExpLoading } = useQuery({
    queryKey: ['expense', 'dashboard', currentYear],
    queryFn: async () => {
      const res = await api.get('/finance/expenses/dashboard', { params: { year: currentYear } });
      return unwrap(res);
    },
  });

  const { data: monthlyRevRows } = useQuery({
    queryKey: ['revenue', 'monthly-by-product', currentYear, productName],
    queryFn: async () => unwrap(await api.get('/finance/revenue/dashboard/monthly-by-product', { 
      params: { year: currentYear, ...(productName && { productName }) } 
    })),
  });

  const { data: recentInvoices = [] } = useQuery({
    queryKey: ['invoices', 'recent'],
    queryFn: async () => {
      try {
        return await listInvoices(0, 5);
      } catch (err) {
        return [];
      }
    },
  });

  const { data: pendingApprovals = [] } = useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: () => listPendingApprovals(),
    retry: false,
  });

  if (isRevLoading || isExpLoading) {
    return <LoadingSpinner label="Loading financial dashboard..." />;
  }

  const d = revenueData || {};
  const eData = expenseData || {};

  const totalRev = Number(d.totalRevenue || 0);
  const totalExp = Number(eData.totalExpense || 0);
  const netBalance = totalRev - totalExp;

  // Process monthly data for Financial Overview chart
  const monthlyDataMap = new Map(SHORT_MONTHS.map((m, i) => [i + 1, { month: m, Revenue: 0, Expenses: 0 }]));

  if (Array.isArray(monthlyRevRows)) {
    monthlyRevRows.forEach(row => {
      const m = Number(row.month);
      if (monthlyDataMap.has(m)) {
        const rev = Number(row.paidRevenue || 0) + Number(row.pendingRevenue || 0) + Number(row.overdueRevenue || 0);
        monthlyDataMap.get(m).Revenue += rev;
      }
    });
  }

  if (Array.isArray(eData.monthlyData)) {
    eData.monthlyData.forEach(row => {
      const m = Number(row.month);
      if (monthlyDataMap.has(m)) {
        monthlyDataMap.get(m).Expenses += Number(row.total || 0);
      }
    });
  }

  const chartData = Array.from(monthlyDataMap.values());

  return (
    <div className="space-y-6">
      {/* Product Filter bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50 border border-slate-200/80 rounded-xl p-3.5">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <FiFilter className="text-blue-600" />
          <span>Dashboard Filters</span>
        </div>
        <div className="w-full sm:w-64">
          <Select
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            className="w-full text-xs"
          >
            <option value="">All Products Overview</option>
            {products?.map((p) => (
              <option key={p.id} value={p.name}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard
          label="Total Revenue"
          value={<CurrencyDisplay value={totalRev} />}
          icon={FiTrendingUp}
          subtitle={`Monthly: ${fmtShort(d.monthlyRevenue || 0)}`}
          onClick={() => navigate('/finance/revenue')}
        />
        <KpiCard
          label="Total Expenses"
          value={<CurrencyDisplay value={totalExp} />}
          icon={FiPieChart}
          subtitle={`Salaries: ${fmtShort(eData.totalSalary || 0)}`}
          onClick={() => navigate('/finance/expenses/dashboard')}
        />
        <KpiCard
          label="Net Profit"
          value={<CurrencyDisplay value={netBalance} />}
          icon={FiDollarSign}
          subtitle={netBalance >= 0 ? 'Surplus Balance' : 'Deficit Balance'}
          onClick={() => navigate('/finance/revenue')}
        />
        <KpiCard
          label="Paid Revenue"
          value={<CurrencyDisplay value={d.paidRevenue || 0} />}
          icon={FiCheckCircle}
          subtitle={`Recurring: ${fmtShort(d.recurringRevenue || 0)}`}
          onClick={() => navigate('/finance/payments')}
        />
        <KpiCard
          label="Pending Invoices"
          value={d.pendingInvoicesCount ?? 0}
          icon={FiFileText}
          subtitle={<CurrencyDisplay value={d.pendingInvoicesAmount || 0} />}
          onClick={() => navigate('/finance/invoices')}
        />
        <KpiCard
          label="Overdue Invoices"
          value={d.overdueInvoicesCount ?? 0}
          icon={FiAlertCircle}
          subtitle={<CurrencyDisplay value={d.overdueInvoicesAmount || 0} />}
          onClick={() => navigate('/finance/invoices')}
        />
      </div>

      {/* Main Grid: Chart + Revenue vs Expense Ratio */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Financial Overview Chart Card */}
        <Card className="lg:col-span-2">
          <CardHeader
            title="Financial Overview"
            subtitle={`Monthly performance comparison for ${currentYear}`}
            actions={
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600">
                  <span className="h-2.5 w-2.5 rounded-full bg-blue-600"></span> Revenue
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 ml-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-slate-600"></span> Expenses
                </span>
              </div>
            }
          />
          <CardBody>
            <div className="h-72 w-full pt-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563EB" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#2563EB" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorExp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#475569" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#475569" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} tickFormatter={fmtShort} />
                  <RechartsTooltip
                    contentStyle={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                    formatter={(value) => [`₹${Number(value).toLocaleString('en-IN')}`, '']}
                  />
                  <Area type="monotone" dataKey="Revenue" stroke="#2563EB" strokeWidth={2.5} fillOpacity={1} fill="url(#colorRev)" />
                  <Area type="monotone" dataKey="Expenses" stroke="#475569" strokeWidth={2} fillOpacity={1} fill="url(#colorExp)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>

        {/* Revenue vs Expenses Summary Comparison Card */}
        <Card>
          <CardHeader title="Revenue vs Expenses" subtitle="Financial ratio breakdown" />
          <CardBody className="space-y-5">
            <div>
              <div className="flex justify-between text-xs font-semibold text-slate-600 mb-1.5">
                <span>Total Revenue</span>
                <span className="text-slate-900 font-bold"><CurrencyDisplay value={totalRev} /></span>
              </div>
              <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-500"
                  style={{ width: `${totalRev + totalExp > 0 ? Math.min(100, Math.round((totalRev / (totalRev + totalExp)) * 100)) : 0}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs font-semibold text-slate-600 mb-1.5">
                <span>Total Expenses</span>
                <span className="text-slate-900 font-bold"><CurrencyDisplay value={totalExp} /></span>
              </div>
              <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-slate-700 rounded-full transition-all duration-500"
                  style={{ width: `${totalRev + totalExp > 0 ? Math.min(100, Math.round((totalExp / (totalRev + totalExp)) * 100)) : 0}%` }}
                ></div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-slate-700">Net Profit</span>
                <span className={`text-base font-bold ${netBalance >= 0 ? 'text-blue-600' : 'text-slate-900'}`}>
                  <CurrencyDisplay value={netBalance} />
                </span>
              </div>
            </div>

            <div className="rounded-xl bg-blue-50/70 p-4 border border-blue-100 text-xs text-blue-900 space-y-2">
              <div className="flex items-center gap-2 font-semibold">
                <FiActivity className="text-blue-600" />
                <span>Collection Efficiency</span>
              </div>
              <p className="text-slate-600">
                Paid revenue accounts for {totalRev > 0 ? Math.round(((d.paidRevenue || 0) / totalRev) * 100) : 0}% of total booked revenue this period.
              </p>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Grid: Recent Transactions + Pending Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Transactions Table Card */}
        <Card className="lg:col-span-2">
          <CardHeader
            title="Recent Transactions"
            subtitle="Latest invoices & payment records"
            actions={
              <button
                type="button"
                onClick={() => navigate('/finance/invoices')}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline"
              >
                View All Invoices &rarr;
              </button>
            }
          />
          <CardBody className="p-0 overflow-x-auto">
            {recentInvoices.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500">
                No recent transactions recorded.
              </div>
            ) : (
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3">Invoice #</th>
                    <th className="px-6 py-3">Customer</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentInvoices.slice(0, 5).map((inv) => (
                    <tr
                      key={inv.id}
                      onClick={() => navigate(`/finance/invoices`)}
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-3.5 font-semibold text-slate-900">
                        {inv.invoiceNumber || `INV-${inv.id}`}
                      </td>
                      <td className="px-6 py-3.5 text-slate-600">
                        {inv.customerName || inv.clientName || 'General Client'}
                      </td>
                      <td className="px-6 py-3.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
                          inv.status === 'PAID'
                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                            : inv.status === 'OVERDUE'
                            ? 'bg-slate-100 text-slate-800 border border-slate-300'
                            : 'bg-slate-50 text-slate-600 border border-slate-200'
                        }`}>
                          {inv.status || 'PENDING'}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 text-right font-bold text-slate-900">
                        <CurrencyDisplay value={inv.totalAmount || inv.amount || 0} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>

        {/* Pending Actions Card */}
        <Card>
          <CardHeader
            title="Pending Actions"
            subtitle={`${pendingApprovals.length} items awaiting review`}
          />
          <CardBody className="p-4 space-y-3">
            {pendingApprovals.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-500">
                <FiCheckCircle className="mx-auto text-2xl text-blue-600 mb-2" />
                No pending workflow items requiring your approval.
              </div>
            ) : (
              pendingApprovals.slice(0, 4).map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:shadow-sm transition-all"
                >
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="text-xs font-semibold text-slate-900 truncate">
                      {item.title || item.entityType || 'Workflow Request'}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Submitted by {item.requesterName || item.createdBy || 'Staff'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/finance/approvals')}
                    className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
                  >
                    Review
                  </button>
                </div>
              ))
            )}

            {pendingApprovals.length > 4 && (
              <button
                type="button"
                onClick={() => navigate('/finance/approvals')}
                className="w-full text-center py-2 text-xs font-semibold text-blue-600 hover:underline"
              >
                View all pending approvals &rarr;
              </button>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function UserDashboard() {
  const navigate = useNavigate();
  const { data: pending } = useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: () => listPendingApprovals(),
    retry: false,
  });
  const pendingCount = pending?.length || 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="My Assets"
          value="Assigned"
          icon={FiBox}
          subtitle="View hardware & equipment"
          onClick={() => navigate('/finance/assets')}
        />
        <KpiCard
          label="My Expenses"
          value="Reimbursements"
          icon={FiCreditCard}
          subtitle="Log a new expense report"
          onClick={() => navigate('/finance/expenses')}
        />
        <KpiCard
          label="Procurement"
          value="Requests"
          icon={FiShoppingCart}
          subtitle="Submit purchase request"
          onClick={() => navigate('/finance/procurement')}
        />
        <KpiCard
          label="Pending Approvals"
          value={pendingCount}
          icon={FiInbox}
          subtitle="Awaiting your action"
          onClick={() => navigate('/finance/approvals')}
        />
      </div>
    </div>
  );
}

function QuickLinks() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const sections = filterNavForUser(can);
  const links = sections.flatMap((s) => s.items).slice(0, 8);

  return (
    <Card>
      <CardHeader title="Quick Navigation" subtitle="Shortcuts to finance modules" />
      <CardBody>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <button
                key={l.key}
                type="button"
                onClick={() => navigate(l.path)}
                className="group flex items-center gap-3 rounded-xl border border-slate-200 p-3.5 text-left text-xs font-semibold text-slate-800 transition-all hover:border-blue-500 hover:bg-blue-50/40 hover:text-blue-600 shadow-sm"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                  <Icon className="text-sm" />
                </div>
                <span className="truncate">{l.label}</span>
              </button>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}

export default function FinanceDashboard() {
  const { username, isSuperadmin, isAdmin } = useAuth();

  return (
    <AppLayout title="Finance Dashboard" breadcrumb="Overview">
      <div className="space-y-6">
        <PageHeader
          title="Finance Dashboard"
          description={
            isSuperadmin
              ? 'Overview of financial performance and activity.'
              : isAdmin
              ? 'Your department finance operations at a glance.'
              : 'Your assigned assets, expenses, and procurement.'
          }
        />

        {isSuperadmin ? <SuperAdminDashboard /> : <UserDashboard />}
        <QuickLinks />
      </div>
    </AppLayout>
  );
}
